import { useMemo } from 'react';
import * as Dfns from 'date-fns/fp';

import { CompetitiveChartBox } from '@Components/CompetitiveChartBox';

import { streamingDate } from './competitive-metrics-constants';
import { filterOutInvalidDays, getCountsByWeek, sortByDate } from './competitive-metrics-utils';
import { CostPersRawData, CreativesRawData } from './use-competitive-metrics-data';

// Mondays of the 26 weeks the charts cover.
const VALID_DAYS = Dfns.eachDayOfInterval({
  start: Dfns.subWeeks(25, streamingDate),
  end: streamingDate
})
  .filter(Dfns.isMonday)
  .map(Dfns.format('yyyy-MM-dd'));

interface ChartsContainerProps {
  costPersRawData?: CostPersRawData;
  creativesRawData?: CreativesRawData;
}

export const ChartsContainer = ({ costPersRawData, creativesRawData }: ChartsContainerProps) => {
  const transformedCreatives = useMemo(() => {
    if (!creativesRawData) {
      return null;
    }
    return sortByDate(Object.entries(creativesRawData).map(([date, count]) => ({ date, count })));
  }, [creativesRawData]);

  const [transformedSpotsByWeek, transformedSpendsByWeek] = useMemo(() => {
    if (!costPersRawData) {
      return [];
    }
    const spotsByWeek = getCountsByWeek(costPersRawData.weeks, 'week', 'spots');
    const spendsByWeek = getCountsByWeek(costPersRawData.weeks, 'week', 'spend');
    return [spotsByWeek, spendsByWeek];
  }, [costPersRawData]);

  const { creativesChartData, spotsChartData, spendsChartData } = useMemo(() => {
    if (transformedCreatives && transformedSpotsByWeek && transformedSpendsByWeek) {
      return {
        creativesChartData: filterOutInvalidDays(transformedCreatives, VALID_DAYS),
        spotsChartData: filterOutInvalidDays(transformedSpotsByWeek, VALID_DAYS),
        spendsChartData: filterOutInvalidDays(transformedSpendsByWeek, VALID_DAYS)
      };
    }
    return {};
  }, [transformedCreatives, transformedSpendsByWeek, transformedSpotsByWeek]);
  return (
    <div className='charts-container'>
      {[
        { data: spendsChartData, title: 'Estimated Spend' },
        { data: spotsChartData, title: 'Spots' },
        { data: creativesChartData, title: 'Creatives' }
      ].map(({ data, title }) => (
        <CompetitiveChartBox key={title} transformedData={sortByDate(data || [])} title={title} />
      ))}
    </div>
  );
};
